/**
 * Devices, the calm way (#1717; the v6 prototype's `devicesUI` / `devRow`).
 *
 * Exceptions first and open: failed, incomplete, couldn't test, not at this
 * site, replaced on the record, and anything with no result after the visit.
 * The fine groups (passed, done, to test) fold to ONE line when they hold
 * more than 3. A row is a real button with aria-expanded; open, it shows the
 * history, the reasons, the tech's words, the photos (the same lightbox as the
 * Results card), the city's side, and the row's existing actions as quiet links.
 *
 * Same data and the same callbacks the Results card gets from detail.tsx.
 * Nothing is fetched here.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Image as ImageIcon, Pencil, Wrench, X } from "lucide-react";
import { ImageLightbox } from "@/components/image-lightbox";
import { api } from "@/lib/api";
import { SIZE_OPTIONS } from "@/lib/assembly-sizes";
import { displayDeviceLocation } from "@/lib/device-location";
import { cn } from "@/lib/utils";
import type { JobPromise } from "@/lib/finish-line";
import { canUsePaper } from "../results-card";
import { FailProtocolFold, type OfficeProtocol } from "../fail-protocol-card";
import { apiErrorInfo, type JobAssetRow, type JobRepairResult, type JobTestResult } from "../types";
import { deviceRowActions, type JobAction } from "../jobActions";
import { RowContextMenu, RowIconButton, RowMoreMenu, RowTools } from "./row-tools";
import {
    buildDevices,
    clockTime,
    deviceCityWords,
    deviceSummary,
    foldLine,
    groupCityWords,
    sectionDevices,
    serialIsModel,
    whenWho,
    type CalmDevice,
} from "./calmModel";
import { FOCUS, OneLine, Quiet, Section, TONE_INK } from "./calm-ui";
import { testStateLabel, testStateTone } from "@/lib/test-state";
import { LegProofChip, legProofFor, type JobProof } from "@/components/proof/job-proof";

export interface CalmDeviceActions {
    onAddDevice: () => void;
    addDisabled: boolean;
    addTitle: string;
    onReconcile?: (() => void) | null;
    onEditAsset: (asset: JobAssetRow) => void;
    onUnlinkAsset: (asset: JobAssetRow) => void;
    unlinkPending: boolean;
    onScopeAsset?: (asset: JobAssetRow, action: "test" | "repair" | "replace") => void;
    scopePendingAssetId?: string | null;
    onUsePaper?: (asset: JobAssetRow) => void;
    onRecordRepair?: (asset: JobAssetRow) => void;
    recordRepairForName?: string | null;
    onRemoveRepair?: (asset: JobAssetRow) => void;
    fieldWorkflowHref?: string | null;
    /** Proof chips S3: how sure each leg is (null/absent = the How-sure switch is off). */
    jobProof?: JobProof | null;
    /** The page's own Upload, for a chip's "Upload the report". */
    onUploadReport?: (() => void) | null;
    /**
     * Job page v2 (slice 3): the row's hover buttons. With these set, ✎ edits
     * in the row (the same PATCH as Edit device), ✕ asks in the row and then
     * unlinks without a second dialog, and ⋯ / right-click open the row menu.
     */
    rowTools?: {
        jobId: string;
        siteId: string | null;
        /** Unlink, already confirmed in the row. */
        onRemoveConfirmed: (asset: JobAssetRow) => void;
    };
}

interface PhotoRef {
    slot: string;
    url: string;
}

function photosOf(test: JobTestResult | undefined): PhotoRef[] {
    if (!test?.photos) return [];
    return Object.entries(test.photos).flatMap(([slot, urls]) => (urls ?? []).map((url) => ({ slot, url })));
}

const humanize = (s: string) => {
    const t = s.replace(/_/g, " ").trim();
    return t.charAt(0).toUpperCase() + t.slice(1);
};

export function CalmDevices({
    assets,
    testResults,
    repairResults,
    postVisit,
    tradeHasFamily,
    failProtocols,
    filing,
    actions,
    initiallyOpen,
    quotedDevicesNudge,
    focus,
}: {
    /** Job page v3: a header tile asked for a group ("pass" opens the passes). */
    focus?: { group: "pass" | "fail" | "none" | "stray"; n: number } | null;
    /** Job page v2: the job came from an estimate (the classic "Add the quoted devices" nudge). */
    quotedDevicesNudge?: boolean;
    assets: JobAssetRow[];
    testResults: JobTestResult[];
    repairResults: JobRepairResult[];
    postVisit: boolean;
    tradeHasFamily: boolean;
    failProtocols?: OfficeProtocol[];
    /** The engine's "filing" promise, for the city's side in words. */
    filing?: JobPromise;
    actions: CalmDeviceActions;
    /** Row/group keys open from the start (the render proof). */
    initiallyOpen?: string[];
}) {
    const [open, setOpen] = useState<Record<string, boolean>>(() =>
        Object.fromEntries((initiallyOpen ?? []).map((k) => [k, true])),
    );
    const [lightbox, setLightbox] = useState<{ photos: PhotoRef[]; index: number } | null>(null);
    // A header tile (Job at a glance): open that group, then bring it into view.
    // Failed and not-tested-after-the-visit rows are always open at the top.
    useEffect(() => {
        if (!focus) return;
        const keys =
            focus.group === "pass" ? ["group:pass", "group:done"] : focus.group === "none" ? ["group:todo"] : [];
        if (keys.length > 0) setOpen((o) => ({ ...o, ...Object.fromEntries(keys.map((k) => [k, true])) }));
        const target =
            focus.group === "pass"
                ? '[data-testid="calm-group-pass"]'
                : focus.group === "stray"
                  ? '[data-testid="calm-orphan-test"]'
                  : null;
        // After the group has opened.
        // Cleared on unmount: a scroll that fires after the page (or a test's
        // DOM) is gone threw "document is not defined" (flaky CI, 11/3).
        const timer = window.setTimeout(() => {
            if (typeof document === "undefined") return;
            const el = target ? document.querySelector(target) : null;
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            else document.getElementById("calm-devices")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
        return () => window.clearTimeout(timer);
        // Only a new ask opens and scrolls.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focus?.n]);
    const devices = buildDevices(assets, testResults, repairResults, postVisit);
    const { look, groups } = sectionDevices(devices);
    const protocolsBy = new Map<string, OfficeProtocol[]>();
    for (const p of failProtocols ?? []) protocolsBy.set(p.asset_id, [...(protocolsBy.get(p.asset_id) ?? []), p]);
    const orphanTests = testResults.filter((t) => !assets.some((a) => a.asset_id === t.asset_id));
    // Job page v2 (slice 5): what the classic Results card said that the calm
    // list had dropped.
    const orphanProtocols = (failProtocols ?? []).filter((p) => !assets.some((a) => a.asset_id === p.asset_id));
    const siteFallbackCount =
        !postVisit && testResults.length === 0 ? assets.filter((a) => a.linked_via === "wo_site_fallback").length : 0;
    const notes = (
        <>
            {quotedDevicesNudge && assets.length === 0 && (
                <p
                    className="m-0 border-t py-2.5 text-[14.5px] text-amber-800 dark:text-amber-300"
                    data-testid="calm-quoted-nudge"
                >
                    <b className="font-semibold">Add the quoted devices.</b> This job came from an approved estimate,
                    but no device serials could be read from the quote. Link the devices it covers before dispatch.{" "}
                    <Quiet onClick={actions.onAddDevice} disabled={actions.addDisabled}>
                        Add device
                    </Quiet>
                </p>
            )}
            {siteFallbackCount > 0 && (
                <p
                    className="m-0 border-t py-2.5 text-[14.5px] text-amber-800 dark:text-amber-300"
                    data-testid="calm-site-fallback"
                >
                    <b className="font-semibold">
                        {siteFallbackCount} device{siteFallbackCount === 1 ? "" : "s"} came from the location, not the
                        work order.
                    </b>{" "}
                    The work order didn&apos;t list any, so the location&apos;s active devices were added. Confirm the
                    list before dispatch and remove anything this trip doesn&apos;t cover.
                </p>
            )}
        </>
    );
    const orphanProtocolRows = orphanProtocols.map((p) => (
        <div key={p.id} className="border-t py-2.5 text-[14.5px]" data-testid="calm-orphan-protocol">
            <span className="text-amber-800 dark:text-amber-300">
                Device no longer linked to this job. Its fail protocol still needs closing.
            </span>
            <FailProtocolFold protocol={p} />
        </div>
    ));

    const toggle = (key: string, fallback: boolean) => setOpen((o) => ({ ...o, [key]: !(o[key] ?? fallback) }));
    const isOpen = (key: string, fallback: boolean) => open[key] ?? fallback;

    const headerActions = (
        <>
            {actions.fieldWorkflowHref && (
                <a
                    href={actions.fieldWorkflowHref}
                    className={cn("text-[14px] font-medium text-muted-foreground hover:underline", FOCUS)}
                >
                    Tech workflow ↗
                </a>
            )}
            {actions.onReconcile && (
                <Quiet tone="mute" onClick={actions.onReconcile}>
                    Reconcile
                </Quiet>
            )}
            <Quiet onClick={actions.onAddDevice} disabled={actions.addDisabled} title={actions.addTitle}>
                Add device
            </Quiet>
        </>
    );

    if (assets.length === 0 && orphanTests.length === 0) {
        return (
            <>
                <OneLine
                    id="calm-devices"
                    title="Devices"
                    words={
                        tradeHasFamily ? "none on this job yet" : "this job's trade isn't set, so no devices are shown"
                    }
                    actions={headerActions}
                />
                {notes}
                {orphanProtocolRows}
            </>
        );
    }

    const row = (d: CalmDevice, key: string, fallback: boolean) => (
        <DeviceRow
            key={key}
            d={d}
            rowKey={key}
            open={isOpen(key, fallback)}
            onToggle={() => toggle(key, fallback)}
            filing={filing}
            protocols={protocolsBy.get(d.asset.asset_id)}
            actions={actions}
            onPhoto={(photos, index) => setLightbox({ photos, index })}
        />
    );

    return (
        <Section id="calm-devices" title="Devices" summary={deviceSummary(devices)} actions={headerActions}>
            {notes}
            <div data-testid="calm-devices-look">{look.map((d) => row(d, `look:${d.asset.link_id}`, true))}</div>
            {groups.map((g) => {
                const key = `group:${g.key}`;
                const groupOpen = isOpen(key, !g.folds);
                if (!groupOpen) {
                    const city = g.key === "pass" ? groupCityWords(filing) : null;
                    return (
                        <button
                            key={key}
                            type="button"
                            className={cn(
                                "flex w-full items-baseline gap-2.5 border-t py-2.5 text-left text-[14.5px] text-foreground/80",
                                FOCUS,
                            )}
                            aria-expanded={false}
                            onClick={() => toggle(key, !g.folds)}
                            data-testid={`calm-fold-${g.key}`}
                        >
                            <span
                                className={cn(
                                    "w-[118px] shrink-0 text-[13.5px] font-semibold",
                                    g.key === "todo" ? TONE_INK.todo : TONE_INK.pass,
                                )}
                            >
                                {g.label}
                            </span>
                            <span className="min-w-0">
                                <b className="font-semibold text-foreground">{foldLine(g).split(" · ")[0]}</b>
                                {foldLine(g).includes(" · ") && ` · ${foldLine(g).split(" · ").slice(1).join(" · ")}`}
                                {city && ` · ${city}`}
                            </span>
                            <span className="ml-auto shrink-0 text-[14px] font-semibold text-primary">Show all ▸</span>
                        </button>
                    );
                }
                return (
                    <div key={key} data-testid={`calm-group-${g.key}`}>
                        {g.rows.map((d) => row(d, `${g.key}:${d.asset.link_id}`, false))}
                        {g.folds && (
                            <button
                                type="button"
                                className={cn(
                                    "flex w-full items-baseline gap-2.5 border-t py-2.5 text-left text-[14px] text-muted-foreground",
                                    FOCUS,
                                )}
                                aria-expanded
                                onClick={() => toggle(key, !g.folds)}
                            >
                                <span className="w-[118px] shrink-0" />
                                <span>Showing all {g.rows.length}</span>
                                <span className="ml-auto font-semibold text-primary">Fold ▴</span>
                            </button>
                        )}
                    </div>
                );
            })}
            {orphanTests.map((t) => (
                <div
                    key={t.id}
                    className="grid grid-cols-[118px_minmax(0,1fr)] gap-3.5 border-t py-2.5 text-[14.5px]"
                    data-testid="calm-orphan-test"
                >
                    <span
                        className={cn(
                            "text-[13.5px] font-semibold",
                            testStateTone(t.overall_result) === "pass"
                                ? TONE_INK.pass
                                : testStateTone(t.overall_result) === "fail"
                                  ? TONE_INK.fail
                                  : TONE_INK.warn,
                        )}
                    >
                        {testStateTone(t.overall_result) === "pass"
                            ? "✓ Passed"
                            : testStateLabel(t.overall_result) || "Recorded"}
                    </span>
                    <span className="min-w-0">
                        <span className="font-semibold">
                            {[t.size_label, t.manufacturer, t.model].filter(Boolean).join(" ") || t.asset_name}
                        </span>
                        {t.serial_number && (
                            <span className="ml-1.5 font-mono text-[12.5px] text-muted-foreground">
                                {t.serial_number}
                            </span>
                        )}
                        <span className="block text-[13px] text-muted-foreground">
                            {new Date(t.performed_at).toLocaleDateString()} · no longer linked to this job (most often
                            its replacement was installed). The result stays on file.
                        </span>
                        <OtherSite test={t} />
                    </span>
                </div>
            ))}
            {orphanProtocolRows}
            {lightbox && (
                <ImageLightbox
                    photos={lightbox.photos.map((p) => ({ src: p.url, alt: p.slot.replace(/_/g, " ") }))}
                    initialIndex={lightbox.index}
                    onClose={() => setLightbox(null)}
                />
            )}
        </Section>
    );
}

function DeviceRow({
    d,
    rowKey,
    open,
    onToggle,
    filing,
    protocols,
    actions,
    onPhoto,
}: {
    d: CalmDevice;
    rowKey: string;
    open: boolean;
    onToggle: () => void;
    filing?: JobPromise;
    protocols?: OfficeProtocol[];
    actions: CalmDeviceActions;
    onPhoto: (photos: PhotoRef[], index: number) => void;
}) {
    const a = d.asset;
    const name = [a.size_label, a.manufacturer, a.model].filter(Boolean).join(" · ") || a.label;
    const location = displayDeviceLocation(a.location_description);
    const panelId = `calm-dev-${rowKey.replace(/[^a-z0-9-]/gi, "-")}`;
    const [mode, setMode] = useState<"edit" | "remove" | null>(null);
    const tools = actions.rowTools;
    const photos = photosOf(d.test);
    const action = (a.required_action || "test").toLowerCase();
    const failed = lowerEq(d.test?.overall_result, "fail");
    const canRecordRepair = !!actions.onRecordRepair && action === "repair" && !d.repair;
    const canScope = !!actions.onScopeAsset && !d.test && !d.inheritedTest && !d.repair && !a.resolved;
    const openRow = () => {
        if (!open) onToggle();
    };
    const rowActions: JobAction[] = tools
        ? deviceRowActions(
              {
                  failed,
                  canRecordRepair,
                  hasRepair: !!d.repair,
                  canUsePaper: !!actions.onUsePaper && canUsePaper(a, d.test),
                  canScope,
                  hasPhotos: photos.length > 0,
                  hasProtocol: failed || (protocols ?? []).length > 0,
                  canRemove: !actions.unlinkPending,
              },
              {
                  "edit-device": () => setMode("edit"),
                  "record-repair": actions.onRecordRepair ? () => actions.onRecordRepair!(a) : undefined,
                  "remove-repair": actions.onRemoveRepair ? () => actions.onRemoveRepair!(a) : undefined,
                  "use-paper": actions.onUsePaper ? () => actions.onUsePaper!(a) : undefined,
                  "visit-kind": actions.onScopeAsset ? openRow : undefined,
                  "device-photos": () => onPhoto(photos, 0),
                  "fail-protocol": openRow,
                  "remove-device": () => setMode("remove"),
              },
          )
        : [];

    const head = (
        <div className="group/row relative">
            <button
                type="button"
                className={cn(
                    "grid w-full grid-cols-[104px_minmax(0,1fr)_16px] items-baseline gap-x-3.5 gap-y-0.5 border-t py-2.5 text-left md:grid-cols-[118px_minmax(0,1.4fr)_minmax(0,1.2fr)_120px_16px]",
                    FOCUS,
                )}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={onToggle}
            >
                <span className={cn("text-[13.5px] font-semibold", TONE_INK[d.tone])}>{d.word}</span>
                <span className="min-w-0">
                    <span className="text-[14.5px] font-semibold text-foreground">{name}</span>
                    {a.serial_number && (
                        <span className="ml-1.5 font-mono text-[12.5px] text-muted-foreground">
                            {a.serial_number}
                            {serialIsModel(a) && (
                                <span className="text-red-700 dark:text-red-400"> (model, not a serial)</span>
                            )}
                        </span>
                    )}
                    {a.parent_asset_id && (
                        <span className="ml-1.5 text-[13px] text-muted-foreground">
                            bypass of {a.parent_serial_number ?? "its main"}
                        </span>
                    )}
                </span>
                <span className="hidden min-w-0 text-[13.5px] text-muted-foreground md:block">{location}</span>
                <span className="hidden text-[13.5px] text-muted-foreground md:block">{whenWho(d)}</span>
                <span className="text-[11px] text-muted-foreground" aria-hidden>
                    {open ? "▾" : "▸"}
                </span>
            </button>
            {tools && (
                <RowTools label={`Actions for ${a.serial_number ?? name}`}>
                    <RowIconButton title="Edit device" onClick={() => setMode("edit")} testId="row-edit">
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                    </RowIconButton>
                    {(canRecordRepair || failed) && (
                        <RowIconButton
                            title={
                                canRecordRepair ? "Record repair" : "Fail protocol: quote, NTE, decline, return visit"
                            }
                            onClick={() => (canRecordRepair ? actions.onRecordRepair!(a) : openRow())}
                            testId="row-repair"
                        >
                            <Wrench className="h-3.5 w-3.5" aria-hidden />
                        </RowIconButton>
                    )}
                    {photos.length > 0 && (
                        <RowIconButton
                            title={`See photos (${photos.length})`}
                            onClick={() => onPhoto(photos, 0)}
                            testId="row-photos"
                        >
                            <ImageIcon className="h-3.5 w-3.5" aria-hidden />
                        </RowIconButton>
                    )}
                    <RowIconButton
                        title="Remove from job"
                        danger
                        disabled={actions.unlinkPending}
                        onClick={() => setMode("remove")}
                        testId="row-remove"
                    >
                        <X className="h-3.5 w-3.5" aria-hidden />
                    </RowIconButton>
                    <RowMoreMenu actions={rowActions} label={`More for ${a.serial_number ?? name}`} testId="row-more" />
                </RowTools>
            )}
        </div>
    );

    return (
        <div data-testid={`calm-device-${a.link_id}`} data-group={d.group}>
            {tools ? (
                <RowContextMenu actions={rowActions} testId="row-context-menu">
                    {head}
                </RowContextMenu>
            ) : (
                head
            )}
            {/* Proof chips S3: how sure this leg's result is (switch on only).
                Outside the row's button, so it taps on its own. */}
            {legProofFor(actions.jobProof ?? null, d.test, d.inheritedTest) && (
                <div className="-mt-1 pb-2 pl-[118px] md:pl-[132px]" data-testid="calm-leg-proof">
                    <LegProofChip
                        test={d.test}
                        inheritedTest={d.inheritedTest}
                        asset={a}
                        jobProof={actions.jobProof ?? null}
                        onUploadReport={actions.onUploadReport ?? null}
                    />
                </div>
            )}
            {tools && mode === "edit" && (
                <InlineDeviceEdit
                    asset={a}
                    jobId={tools.jobId}
                    siteId={tools.siteId}
                    onDone={() => setMode(null)}
                    onAllFields={() => {
                        setMode(null);
                        actions.onEditAsset(a);
                    }}
                />
            )}
            {tools && mode === "remove" && (
                <div
                    className="mb-2 grid gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-[14px] dark:border-red-900 dark:bg-red-500/10 md:ml-[132px]"
                    data-testid="row-remove-confirm"
                >
                    <b className="font-semibold text-foreground">
                        Remove {a.serial_number ? `S/N ${a.serial_number}` : name} from this job?
                    </b>
                    <span className="text-foreground/80">It stays at the location. Its test stays in history.</span>
                    <span className="flex flex-wrap gap-x-4">
                        <Quiet
                            tone="danger"
                            disabled={actions.unlinkPending}
                            onClick={() => {
                                setMode(null);
                                tools.onRemoveConfirmed(a);
                            }}
                            data-testid="row-remove-go"
                        >
                            Remove from job
                        </Quiet>
                        <Quiet tone="mute" onClick={() => setMode(null)}>
                            Keep it
                        </Quiet>
                    </span>
                </div>
            )}
            {open && (
                <DeviceDetail
                    id={panelId}
                    d={d}
                    filing={filing}
                    protocols={protocols}
                    actions={actions}
                    location={location}
                    onPhoto={onPhoto}
                />
            )}
        </div>
    );
}

/** ✎ in the row: the fields people fix most, the same PATCH as Edit device. */
function InlineDeviceEdit({
    asset,
    jobId,
    siteId,
    onDone,
    onAllFields,
}: {
    asset: JobAssetRow;
    jobId: string;
    siteId: string | null;
    onDone: () => void;
    onAllFields: () => void;
}) {
    const qc = useQueryClient();
    const [serial, setSerial] = useState(asset.serial_number ?? "");
    const [make, setMake] = useState(asset.manufacturer ?? "");
    const [model, setModel] = useState(asset.model ?? "");
    const [size, setSize] = useState(asset.size_label ?? "");
    const [where, setWhere] = useState(asset.location_description ?? "");
    const body: Record<string, string | null> = {};
    const str = (v: string) => v.trim() || null;
    if (serial !== (asset.serial_number ?? "")) body.serialNumber = str(serial);
    if (make !== (asset.manufacturer ?? "")) body.manufacturer = str(make);
    if (model !== (asset.model ?? "")) body.model = str(model);
    if (size !== (asset.size_label ?? "")) body.sizeLabel = str(size);
    if (where !== (asset.location_description ?? "")) body.locationDescription = str(where);
    const changed = Object.keys(body).length > 0;
    const save = useMutation({
        mutationFn: () => api.patch(`/api/assets/${asset.asset_id}`, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            if (siteId) qc.invalidateQueries({ queryKey: ["site-assets", siteId] });
            toast.success(
                body.serialNumber !== undefined
                    ? `Serial changed to ${body.serialNumber ?? "(blank)"}`
                    : "Device updated",
            );
            onDone();
        },
        onError: (err: unknown) => {
            const info = apiErrorInfo(err);
            toast.error(info.message ? `Couldn't update device: ${info.message}` : "Couldn't update device");
        },
    });
    const field = "h-8 w-full rounded border border-border bg-background px-2 text-[14px] text-foreground";
    const label = "grid gap-1 text-[12.5px] font-medium text-muted-foreground";
    return (
        <form
            className="mb-2 grid gap-2.5 rounded-lg border border-border bg-muted/40 px-3 py-3 md:ml-[132px]"
            data-testid="row-edit-form"
            onSubmit={(e) => {
                e.preventDefault();
                if (changed) save.mutate();
            }}
        >
            <div className="grid gap-2.5 sm:grid-cols-2">
                <label className={label}>
                    Serial number
                    <input
                        className={cn(field, "font-mono")}
                        value={serial}
                        onChange={(e) => setSerial(e.target.value)}
                        autoFocus
                        data-testid="row-edit-serial"
                    />
                </label>
                <label className={label}>
                    Size
                    <select className={field} value={size} onChange={(e) => setSize(e.target.value)}>
                        <option value="">None</option>
                        {[...new Set([...(size ? [size] : []), ...SIZE_OPTIONS])].map((o) => (
                            <option key={o} value={o}>
                                {o}
                            </option>
                        ))}
                    </select>
                </label>
                <label className={label}>
                    Make
                    <input className={field} value={make} onChange={(e) => setMake(e.target.value)} />
                </label>
                <label className={label}>
                    Model
                    <input className={field} value={model} onChange={(e) => setModel(e.target.value)} />
                </label>
                <label className={cn(label, "sm:col-span-2")}>
                    Where it is
                    <input className={field} value={where} onChange={(e) => setWhere(e.target.value)} />
                </label>
            </div>
            <span className="flex flex-wrap items-baseline gap-x-4">
                <button
                    type="submit"
                    disabled={!changed || save.isPending}
                    className={cn(
                        "rounded-md border border-border bg-card px-3 py-1 text-[14px] font-semibold text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground",
                        FOCUS,
                    )}
                    data-testid="row-edit-save"
                >
                    {save.isPending ? "Saving…" : "Save"}
                </button>
                <Quiet tone="mute" onClick={onDone}>
                    Cancel
                </Quiet>
                <Quiet tone="mute" onClick={onAllFields} className="ml-auto">
                    All fields (type, use, meter, bypass)…
                </Quiet>
            </span>
        </form>
    );
}

function DeviceDetail({
    id,
    d,
    filing,
    protocols,
    actions,
    location,
    onPhoto,
}: {
    id: string;
    d: CalmDevice;
    filing?: JobPromise;
    protocols?: OfficeProtocol[];
    actions: CalmDeviceActions;
    location: string | null;
    onPhoto: (photos: PhotoRef[], index: number) => void;
}) {
    const a = d.asset;
    const t = d.test;
    const shown = t ?? d.inheritedTest;
    const photos = photosOf(t);
    const reasons = (t?.failure_reasons ?? []).filter((r): r is string => !!r && r.trim().length > 0);
    const narrative = [t?.completion_notes, t?.failure_notes]
        .map((s) => (s ?? "").trim())
        .filter((s, i, arr) => s.length > 0 && arr.indexOf(s) === i);
    const history: Array<{ at: string; text: string }> = [];
    if (d.repair) {
        history.push({
            at: d.repair.created_at,
            text: `Repair recorded${d.repair.performed_by_name ? ` by ${d.repair.performed_by_name}` : ""}${
                d.repair.entered_by_name && d.repair.entered_by_name !== d.repair.performed_by_name
                    ? `, entered by ${d.repair.entered_by_name}`
                    : ""
            }`,
        });
    }
    if (shown) {
        const by = shown.performed_by_name || shown.tester_name;
        history.push({
            at: shown.performed_at,
            text: `${d.inheritedTest ? "Tested with its main" : `Tested: ${(testStateLabel(shown.overall_result) || "recorded").toLowerCase()}`}${by ? ` by ${by}` : ""}${
                shown.entered_by_name && shown.entered_by_name !== shown.performed_by_name
                    ? `, entered by ${shown.entered_by_name}`
                    : ""
            }`,
        });
    }
    history.sort((x, y) => (x.at < y.at ? -1 : 1));
    const city = deviceCityWords(filing, d);
    const action = (a.required_action || "test").toLowerCase();
    const canScope = actions.onScopeAsset && !t && !d.inheritedTest && !d.repair && !a.resolved;

    return (
        <div
            id={id}
            className="grid gap-2 pb-3.5 text-[14.5px] text-foreground/80 md:pl-[132px]"
            data-testid="calm-device-detail"
        >
            {location && <span className="text-[13.5px] text-muted-foreground md:hidden">{location}</span>}
            {history.length > 0 && (
                <ul className="m-0 grid list-none gap-0.5 p-0">
                    {history.map((h) => (
                        <li key={`${h.at}-${h.text}`} className="grid grid-cols-[72px_minmax(0,1fr)] gap-2">
                            <span className="text-[13px] text-muted-foreground">{clockTime(h.at)}</span>
                            <span>{h.text}</span>
                        </li>
                    ))}
                </ul>
            )}
            <OtherSite test={t} />
            {a.disposition_reason && (
                <blockquote className="m-0 border-l-2 border-foreground/20 pl-3">
                    “{a.disposition_reason}”
                    <span className="block text-[13px] text-muted-foreground">The tech&apos;s reason</span>
                </blockquote>
            )}
            {(reasons.length > 0 || narrative.length > 0 || t?.flagged_for_repair) &&
                (lowerEq(t?.overall_result, "fail") ? (
                    <div className="text-red-800 dark:text-red-300">
                        <b className="font-semibold">
                            Why it failed{t?.flagged_for_repair ? " · flagged for repair" : ""}.
                        </b>{" "}
                        {reasons.map(humanize).join("; ")}
                        {narrative.map((n) => (
                            <span key={n} className="mt-0.5 block whitespace-pre-wrap text-foreground/80">
                                {n}
                            </span>
                        ))}
                    </div>
                ) : (
                    narrative.map((n) => (
                        <p key={n} className="m-0 whitespace-pre-wrap">
                            {n}
                        </p>
                    ))
                ))}
            {t?.recommended_action && <span>Recommendation: {t.recommended_action.replace(/_/g, " ")}</span>}
            {/* An "Unable to locate assembly" FAIL stores its reason as the
                remarks too (10/29): it already shows under Why it failed. */}
            {t?.remarks && !reasons.includes(t.remarks.trim()) && <span className="italic">{t.remarks}</span>}
            {d.repair?.repair_notes?.trim() && (
                <blockquote className="m-0 whitespace-pre-wrap border-l-2 border-foreground/20 pl-3">
                    {d.repair.repair_notes.trim()}
                    <span className="block text-[13px] text-muted-foreground">Repair notes</span>
                </blockquote>
            )}
            {d.repair?.photos_skipped_reason && (
                <span className="text-amber-800 dark:text-amber-300">
                    No repair photos: {d.repair.photos_skipped_reason}
                </span>
            )}
            {shown?.entry_source === "paper_conversion" && (
                <span className="text-amber-800 dark:text-amber-300">
                    From the signed paper report
                    {shown.converted_by_name ? `, entered by ${shown.converted_by_name}` : ""}
                    {shown.conversion_reason ? `. ${shown.conversion_reason}` : ""}
                </span>
            )}
            {a.needs_verification_reason && (
                <span className="text-amber-800 dark:text-amber-300">
                    Verify on site: {a.needs_verification_reason}
                </span>
            )}
            {(protocols ?? []).map((p) => (
                <FailProtocolFold key={p.id} protocol={p} />
            ))}
            {photos.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="calm-photo-strip">
                    {photos.map((p, i) => (
                        <button
                            key={`${p.slot}-${i}`}
                            type="button"
                            className={cn("h-14 w-16 overflow-hidden rounded-md bg-muted", FOCUS)}
                            onClick={() => onPhoto(photos, i)}
                            title={p.slot.replace(/_/g, " ")}
                            aria-label={`Photo: ${p.slot.replace(/_/g, " ")}`}
                        >
                            <img src={p.url} alt="" className="h-full w-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
            {city && <span className="text-[13.5px] text-muted-foreground">{city}</span>}
            {canScope && (
                <label className="flex items-baseline gap-2 text-[13.5px] text-muted-foreground">
                    This visit:
                    <select
                        className="h-7 rounded border border-border bg-background px-1 text-[13.5px] text-foreground"
                        value={action}
                        disabled={actions.scopePendingAssetId === a.asset_id}
                        onChange={(e) => actions.onScopeAsset!(a, e.target.value as "test" | "repair" | "replace")}
                    >
                        <option value="test">Test</option>
                        <option value="repair">Repair</option>
                        <option value="replace">Replace</option>
                    </select>
                </label>
            )}
            <span className="flex flex-wrap gap-x-4 gap-y-1">
                {actions.onRecordRepair && action === "repair" && !d.repair && (
                    <Quiet onClick={() => actions.onRecordRepair!(a)}>
                        {actions.recordRepairForName
                            ? `Record repair for ${actions.recordRepairForName}`
                            : "Record repair"}
                    </Quiet>
                )}
                {actions.onUsePaper && canUsePaper(a, t) && (
                    <Quiet onClick={() => actions.onUsePaper!(a)}>Use the paper</Quiet>
                )}
                {actions.onRemoveRepair && d.repair && (
                    <Quiet tone="danger" onClick={() => actions.onRemoveRepair!(a)}>
                        Remove repair
                    </Quiet>
                )}
                <Quiet tone="mute" onClick={() => actions.onEditAsset(a)}>
                    Edit device
                </Quiet>
                <Quiet tone="mute" disabled={actions.unlinkPending} onClick={() => actions.onUnlinkAsset(a)}>
                    Remove from job
                </Quiet>
            </span>
        </div>
    );
}

const lowerEq = (s: string | null | undefined, v: string) => (s ?? "").toLowerCase() === v;

/** Sunny Woods #092678193: a test saved on this job for a device at ANOTHER site (the Results card's line). */
function OtherSite({ test }: { test: JobTestResult | undefined }) {
    if (!test?.other_site_id) return null;
    return (
        <span className="block text-[13.5px] text-amber-800 dark:text-amber-300" data-testid="calm-other-site">
            Test for a device at another site:{" "}
            <Link to={`/sites/${test.other_site_id}`} className="font-semibold underline">
                {test.other_site_name || "open that site"}
            </Link>
            . It doesn&apos;t hold this job&apos;s paperwork.
        </span>
    );
}
